'use client';
import React, { useState } from 'react';
import { Search, MapPin, ArrowRight, Shield, ChevronRight, Settings } from 'lucide-react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';

interface Machine {
  id: string;
  title: string;
  price: number;
  location: string;
  condition: string;
  category: string;
  seller: string;
  verified: boolean;
  specs: string;
  posted: string;
}

const MACHINES: Machine[] = [
  { id: '1', title: 'Caterpillar 320D Excavator 2015', price: 4500000, location: 'Nairobi', condition: 'Used', category: 'Excavators', seller: 'Heavy Mach Kenya', verified: true, specs: '20T, 120HP, 6,500 hrs', posted: '1 day ago' },
  { id: '2', title: 'Komatsu D65 Bulldozer', price: 3800000, location: 'Mombasa', condition: 'Used', category: 'Bulldozers', seller: 'Coast Machinery', verified: true, specs: '15T blade, 170HP', posted: '3 days ago' },
  { id: '3', title: 'Industrial Maize Mill 5T/hr', price: 850000, location: 'Nakuru', condition: 'New', category: 'Agri Machinery', seller: 'Agri Solutions', verified: true, specs: '5 tons/hr, 3-phase electric', posted: '2 hrs ago' },
  { id: '4', title: 'Concrete Mixer 500L Diesel', price: 180000, location: 'Nairobi', condition: 'New', category: 'Construction', seller: 'BuildTech Kenya', verified: false, specs: '500L drum, 10HP diesel', posted: '5 hrs ago' },
  { id: '5', title: 'Toyota Forklift 3T Diesel 2018', price: 1200000, location: 'Nairobi', condition: 'Used', category: 'Forklifts', seller: 'Heavy Mach Kenya', verified: true, specs: '3T capacity, 4.5m lift', posted: '2 days ago' },
  { id: '6', title: 'Borehole Drilling Rig 200m', price: 2800000, location: 'Kisumu', condition: 'Used', category: 'Drilling', seller: 'Water Solutions', verified: true, specs: '200m depth, truck-mounted', posted: '1 week ago' },
];

const CATEGORIES = ['All', 'Excavators', 'Bulldozers', 'Agri Machinery', 'Construction', 'Forklifts', 'Drilling'];
const CONDITIONS = ['All', 'New', 'Used'];

export default function MachineryPage() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [condition, setCondition] = useState('All');

  const filtered = MACHINES.filter(m => {
    const matchSearch = !search || m.title.toLowerCase().includes(search.toLowerCase());
    const matchCat = category === 'All' || m.category === category;
    const matchCond = condition === 'All' || m.condition === condition;
    return matchSearch && matchCat && matchCond;
  });

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ background: '#070C1A' }}>
        <div className="relative px-4 pt-8 pb-10 overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #0a1a0a 0%, #0d1520 50%, #070C1A 100%)' }}>
          <div className="absolute inset-0 opacity-20"
            style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #FFB300 0%, transparent 50%), radial-gradient(circle at 70% 30%, #FF6D00 0%, transparent 50%)' }} />
          <div className="relative max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-600 mb-4"
              style={{ background: 'rgba(255,179,0,0.15)', color: '#FFB300', border: '1px solid rgba(255,179,0,0.3)' }}>
              <Settings size={12} /> Heavy Machinery & Equipment
            </div>
            <h1 className="text-3xl font-800 text-white mb-3 leading-tight">
              Buy & Sell <span style={{ color: '#FFB300' }}>Machinery</span> in Kenya
            </h1>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Excavators, bulldozers, agri equipment, and industrial machines. Verified sellers.
            </p>
            <div className="relative max-w-md mx-auto">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.4)' }} />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search machinery…"
                className="w-full pl-10 pr-4 py-3.5 rounded-2xl text-sm outline-none"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }} />
            </div>
          </div>
        </div>

        <div className="px-4 py-3 sticky top-0 z-20"
          style={{ background: 'rgba(7,12,26,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-4xl mx-auto flex items-center gap-2 overflow-x-auto pb-1">
            {CATEGORIES.map(c => (
              <button key={c} onClick={() => setCategory(c)}
                className="px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
                style={{ background: category === c ? 'rgba(255,179,0,0.15)' : 'rgba(255,255,255,0.04)', color: category === c ? '#FFB300' : 'rgba(255,255,255,0.55)', border: `1px solid ${category === c ? 'rgba(255,179,0,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                {c}
              </button>
            ))}
            <div className="flex-shrink-0 w-px h-5 mx-1" style={{ background: 'rgba(255,255,255,0.1)' }} />
            {CONDITIONS.map(c => (
              <button key={c} onClick={() => setCondition(c)}
                className="px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
                style={{ background: condition === c ? 'rgba(0,200,83,0.15)' : 'rgba(255,255,255,0.04)', color: condition === c ? '#00C853' : 'rgba(255,255,255,0.55)', border: `1px solid ${condition === c ? 'rgba(0,200,83,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 pt-4 pb-8">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              <span style={{ color: '#FFB300', fontWeight: 700 }}>{filtered.length}</span> machines found
            </span>
            <Link href="/login"
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-700"
              style={{ background: '#FFB300', color: '#000' }}>
              List Machine <ArrowRight size={12} />
            </Link>
          </div>

          <div className="flex flex-col gap-3">
            {filtered.map(m => (
              <div key={m.id} className="p-4 rounded-2xl"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(255,179,0,0.12)', border: '1px solid rgba(255,179,0,0.2)' }}>
                    <Settings size={20} style={{ color: '#FFB300' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-sm font-700 text-white">{m.title}</h3>
                      <span className="text-sm font-800 flex-shrink-0" style={{ color: '#FFB300' }}>
                        KES {m.price.toLocaleString()}
                      </span>
                    </div>
                    <p className="text-[10px] mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>{m.specs}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className="text-[10px] px-1.5 py-0.5 rounded-md font-600"
                        style={{ background: m.condition === 'New' ? 'rgba(0,200,83,0.15)' : 'rgba(255,179,0,0.15)', color: m.condition === 'New' ? '#00C853' : '#FFB300' }}>
                        {m.condition}
                      </span>
                      <div className="flex items-center gap-1">
                        <MapPin size={10} style={{ color: 'rgba(255,255,255,0.35)' }} />
                        <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{m.location}</span>
                      </div>
                      {m.verified && <Shield size={10} style={{ color: '#00C853' }} />}
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.35)' }}>{m.seller} · {m.posted}</span>
                      <Link href="/login"
                        className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-700"
                        style={{ background: 'rgba(255,179,0,0.15)', color: '#FFB300', border: '1px solid rgba(255,179,0,0.25)' }}>
                        Enquire <ChevronRight size={11} />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-6 rounded-3xl text-center"
            style={{ background: 'linear-gradient(135deg, rgba(255,179,0,0.1), rgba(255,109,0,0.1))', border: '1px solid rgba(255,179,0,0.2)' }}>
            <h3 className="text-lg font-800 text-white mb-2">List Your Machinery</h3>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>Reach serious buyers across Kenya and East Africa.</p>
            <Link href="/login"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-700 text-sm"
              style={{ background: '#FFB300', color: '#000' }}>
              <Settings size={16} /> List Equipment
            </Link>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
